import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { EventV2, IEvent } from '@/utils/Events/events';
import { User } from '@/utils/interfaces/User';

/**
 * useSessionTracking
 *
 * Tracks how many seconds a user is **actively** on a page — i.e. the tab is
 * visible AND the window has focus. When the user navigates away or closes the
 * tab the session event is fired with:
 *   - value          → total active seconds
 *   - activity_start → ISO timestamp when the hook mounted
 *   - activity_end   → ISO timestamp when the session ended
 *
 * Usage (drop-in, no boilerplate needed on the page):
 *
 *   useSessionTracking({
 *     enabled: !!userData,          // only track logged-in users
 *     userData,
 *     payload: {
 *       event_name: 'details_session',
 *       source: 'books',
 *       source_type: landingPageData?.bookType,
 *       source_id: landingPageData?._id,
 *       source_name: ecomProductData?.display_name,
 *       unit: 'seconds',
 *       meta: { product_category: '...', book_total_pages: 123 },
 *     },
 *   });
 */

interface UseSessionTrackingOptions {
  /** The base event payload. value / activity_start / activity_end are filled automatically. */
  payload: Omit<IEvent, 'value' | 'activity_start' | 'activity_end'> & {
    meta?: Record<string, any>;
  };
  userData: User | undefined;
  /** Set to false to disable tracking (e.g. while userData is not yet loaded). */
  enabled?: boolean;
}

const toISOString = (d: Date) => d.toISOString();

const isActive = () =>
  typeof document !== 'undefined' &&
  document.visibilityState === 'visible' &&
  document.hasFocus();

export function useSessionTracking({
  payload,
  userData,
  enabled = true,
}: UseSessionTrackingOptions) {
  const router = useRouter();

  // Stable refs — never cause re-renders
  const startTimeRef = useRef<Date | null>(null);
  const activeSecondsRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    // ── Initialise ──────────────────────────────────────────────────────────
    startTimeRef.current = new Date();
    activeSecondsRef.current = 0;
    firedRef.current = false;

    // Tick every second; only count when tab is visible AND window is focused
    intervalRef.current = setInterval(() => {
      if (isActive()) {
        activeSecondsRef.current += 1;
      }
    }, 1000);

    // ── Fire the session event ───────────────────────────────────────────────
    const fireEvent = () => {
      if (firedRef.current) return; // guard against double-fire
      firedRef.current = true;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      const endTime = new Date();
      const activeSeconds = activeSecondsRef.current;

      // Skip zero-second sessions (tab switch blips, etc.)
      if (activeSeconds <= 0) return;

      EventV2({
        payload: {
          ...payload,
          unit: 'seconds',
          value: activeSeconds,
          activity_start: toISOString(startTimeRef.current!),
          activity_end: toISOString(endTime),
        },
        userData,
        pathname: router.pathname,
      });
    };

    // ── Listeners ────────────────────────────────────────────────────────────

    // Tab close / browser navigation
    window.addEventListener('beforeunload', fireEvent);

    // Next.js client-side route change
    router.events.on('routeChangeStart', fireEvent);

    return () => {
      // Component unmount (also covers Next.js page transitions)
      fireEvent();
      window.removeEventListener('beforeunload', fireEvent);
      router.events.off('routeChangeStart', fireEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
